import { useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { toast } from "sonner"
import { Lock, MessageSquareMore, MoreHorizontal, Pencil, Trash2, X } from "lucide-react"
import { Avatar } from "@/components/ui/avatar"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type { AppuserNote } from "@/types/ticket"

type Props = {
  appuserId: number
  notes: AppuserNote[]
  /** When set, only the first N notes show — with a "View all" link to the appuser detail page. */
  preview?: number
  /** Hidden when rendered as a compact mini-pane on the ticket meta panel. */
  showHeader?: boolean
  /** Disables the create / edit / delete affordances — used on read-only surfaces. */
  readOnly?: boolean
  /** Operator id for "is the author the current user" — controls Edit/Delete visibility. */
  currentUserId?: number | null
  /** Whether the current user is a global admin — admins can edit/delete any note. */
  currentUserAdmin?: boolean
  /** Focus the composer textarea on mount. Used by the ticket meta panel so opening the Notes tab drops the cursor straight into the input. */
  autoFocus?: boolean
}

// Operator-only notes pane. The notes are pinned to the appuser, not the
// convo — they travel WITH THE USER across every ticket they ever open.
// Never serialized to the SDK / s2s.
//
// Used on:
//   - /appusers/:id detail page right rail (full list)
//   - /tickets/:id meta panel (Notes tab — autoFocus on)
//
// Composer model: Enter saves; Shift+Enter inserts a newline; Esc clears.
// Authors get a 15-minute window to edit / delete their own notes (the
// `editableUntil` ISO ts on each row); admins skip the window. Server
// enforces both.
export function AppuserNotesPane({
  appuserId,
  notes,
  preview,
  showHeader = true,
  readOnly = false,
  currentUserId = null,
  currentUserAdmin = false,
  autoFocus = false,
}: Props) {
  const visible = preview ? notes.slice(0, preview) : notes
  const remaining = preview ? Math.max(0, notes.length - preview) : 0
  // Server returns `recent` (newest-first). Chat order is oldest-at-top so
  // scrolling up reveals history — reverse for display.
  const ordered = [...visible].reverse()
  const [draft, setDraft] = useState("")
  const [saving, setSaving] = useState(false)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (autoFocus) composerRef.current?.focus()
  }, [autoFocus])

  // Pin the scroll to the bottom on mount and whenever a new note arrives,
  // so the latest is always in view (matches chat behavior).
  useEffect(() => {
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [notes.length])

  function submit() {
    const body = draft.trim()
    if (!body || saving) return
    setSaving(true)
    router.post(`/appusers/${appuserId}/notes`, { note: { body } }, {
      preserveScroll: true,
      onSuccess: () => setDraft(""),
      onError: () => toast.error("Could not save note."),
      onFinish: () => { setSaving(false); composerRef.current?.focus() },
    })
  }

  function cancel() {
    setDraft("")
    composerRef.current?.blur()
  }

  return (
    <Card variant="inset" className="flex flex-col gap-0 overflow-hidden p-0">
      {showHeader && (
        <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <MessageSquareMore
              size={20}
              strokeWidth={2}
              className="mt-0.5 shrink-0 text-vip-gold"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-app-text">Internal notes</h3>
                <span className="rounded-full bg-app-panel2 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-app-muted">
                  {notes.length}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-app-subtle">
                Private team-only user notes
              </p>
            </div>
          </div>
          {/* Visual placeholder for a future per-pane menu (export / clear /
              etc.). Kept as a no-op button so the header lockup matches the
              spec; wire actions when they exist. */}
          <button
            type="button"
            title="More"
            className="shrink-0 rounded p-1 text-app-muted hover:bg-white/10 hover:text-app-text"
          >
            <MoreHorizontal size={18} strokeWidth={2} />
          </button>
        </div>
      )}

      <div
        ref={listRef}
        className="flex max-h-96 flex-col overflow-y-auto"
      >
        {remaining > 0 && (
          <a
            href={`/appusers/${appuserId}#notes`}
            className="border-b border-border px-4 py-2 text-xs text-app-muted underline-offset-2 hover:text-app-text hover:underline"
          >
            View all ({notes.length})
          </a>
        )}
        {ordered.length === 0 ? (
          <p className="px-4 py-4 text-xs text-app-subtle">
            No notes yet. Type below to add one.
          </p>
        ) : (
          ordered.map((n, i) => {
            const isOwnRecent =
              n.authorId === currentUserId &&
              new Date(n.editableUntil).getTime() > Date.now()
            return (
              <NoteRow
                key={n.id}
                note={n}
                zebra={i % 2 === 0}
                canEdit={!readOnly && (currentUserAdmin || isOwnRecent)}
              />
            )
          })
        )}
      </div>

      {!readOnly && (
        <div className="border-t border-border px-4 py-4">
          {/*
            focus:ring-0 + focus:border-border defeats @tailwindcss/forms,
            which would otherwise paint a bright blue --tw-ring-color halo
            on focus. The plugin is loaded globally in tailwind.config.ts.
          */}
          <textarea
            ref={composerRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                submit()
              }
              if (e.key === "Escape") {
                e.preventDefault()
                cancel()
              }
            }}
            rows={3}
            placeholder="Add an internal note…"
            className="w-full resize-y rounded-control border border-border bg-app-panel2 px-3 py-2 text-sm text-app-text placeholder:text-app-subtle focus:border-border focus:outline-none focus:ring-0"
          />
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-vip-goldSoft">
              <Lock size={12} strokeWidth={2.5} /> Only visible to your team
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={cancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={submit}
                disabled={saving}
              >
                {saving ? "Saving…" : "Add note"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}

function NoteRow({ note, zebra, canEdit }: {
  note: AppuserNote
  zebra: boolean
  canEdit: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(note.body)
  const [saving, setSaving] = useState(false)

  function save() {
    const body = draft.trim()
    if (!body || saving) return
    setSaving(true)
    router.patch(`/appuser_notes/${note.id}`, { note: { body } }, {
      preserveScroll: true,
      onSuccess: () => { setEditing(false); toast.success("Note updated.") },
      onError: () => toast.error("Could not update note."),
      onFinish: () => setSaving(false),
    })
  }

  function destroy() {
    if (!confirm("Delete this note?")) return
    router.delete(`/appuser_notes/${note.id}`, {
      preserveScroll: true,
      onSuccess: () => toast.success("Note deleted."),
      onError: () => toast.error("Could not delete note."),
    })
  }

  return (
    <div
      className={cn(
        "group flex gap-3 px-4 py-4",
        // Zebra striping replaces row separator lines — even-indexed rows
        // sit darker than the Card surface, odd rows inherit the Card's
        // bg-white/5 inset tint for the lift. Tailwind built-ins (black /
        // white) are the only colors whose /opacity modifier actually
        // resolves — the var(--…) custom palette silently no-ops it.
        zebra && "bg-black/40",
      )}
    >
      <Avatar name={note.authorName} url={note.authorAvatarUrl} size={36} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-baseline gap-2">
            <span className="truncate text-xs font-semibold uppercase tracking-wide text-app-text">
              {note.authorName}
            </span>
            <span className="shrink-0 text-[11px] text-app-subtle">
              {fmtCompactRelative(note.createdAt)}
            </span>
          </div>
          {canEdit && !editing && (
            <span className="flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
              <button type="button" onClick={() => setEditing(true)} title="Edit" className="rounded p-0.5 text-app-muted hover:bg-white/10 hover:text-app-text">
                <Pencil size={12} strokeWidth={2} />
              </button>
              <button type="button" onClick={destroy} title="Delete" className="rounded p-0.5 text-app-muted hover:bg-status-error/20 hover:text-status-error">
                <Trash2 size={12} strokeWidth={2} />
              </button>
            </span>
          )}
          {canEdit && editing && (
            <button type="button" onClick={() => { setEditing(false); setDraft(note.body) }} title="Cancel" className="rounded p-0.5 text-app-muted hover:text-app-text">
              <X size={12} strokeWidth={2} />
            </button>
          )}
        </div>
        {editing ? (
          <>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save() }
                if (e.key === "Escape") { e.preventDefault(); setEditing(false); setDraft(note.body) }
              }}
              rows={3}
              className="w-full resize-y rounded-control border border-border bg-app-panel2 px-2 py-1.5 text-sm text-app-text focus:border-border focus:outline-none focus:ring-0"
              autoFocus
            />
            <div className="mt-1 flex justify-end">
              <Button type="button" size="sm" disabled={saving || !draft.trim()} onClick={save}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </>
        ) : (
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-app-text">
            {note.body}
          </div>
        )}
      </div>
    </div>
  )
}

